import { UserOutlined } from "@ant-design/icons";
import { Avatar, Badge, Button, Spin } from "antd";
import { createRef, useCallback, useContext, useEffect, useRef, useState } from "react";
import Chat from "./Chat";

import styles from "./index.module.css";
import { ChatsContext, Chat as ChatType } from "context/chatsContext";
import { TitleComponent } from "Components/Title";
import { TabsComponent } from "Components/TabsComponent";
import { useSearchParams } from "react-router-dom";
import { PROP_NAME_CHOOSEN_CHAT_ID, PROP_NAME_CHOOSEN_USER_CHAT_ID, PROP_NAME_MAIN_TAB } from "helpers/constants";
import InfiniteScroll from "react-infinite-scroll-component";
import { SocketContext } from "context/socketContext";
import { useRequest, useUpdateEffect } from "ahooks";
import { AdminContext } from "context/adminContext";
import { INIT_CHATS } from "helpers/helper";
import { formatLastMessageDate } from "helpers/format";
import { getSupportChatBadges } from "helpers/backend_helper";
import { NotificationContext } from "context/notificationContext";
import { getAvatarSrc } from "Components/UserNameRatingPhotoComponent/UserNameRatingPhotoComponent";

export enum STATUSES {
  WAIT = "WAIT",
  IN_PROGRESS = "IN_PROGRESS",
  FINISHED = "FINISHED",
}

const getItems = ({
  finished_count,
  in_progress_count,
  wait_count,
}: {
  finished_count: string;
  in_progress_count: string;
  wait_count: string;
}) => [
  {
    key: "0",
    label: (
      <div className={styles.tabTextWrapper}>
        <div className={styles.deals}>Requests</div>
        <div>{wait_count}</div>
      </div>
    ),
  },
  {
    key: "1",
    label: (
      <div className={styles.tabTextWrapper}>
        <div className={styles.deals}>Taken</div>
        <div>{in_progress_count}</div>
      </div>
    ),
  },
  {
    key: "2",
    label: (
      <div className={styles.tabTextWrapper}>
        <div className={styles.deals}>Completed</div>
        <div>{finished_count}</div>
      </div>
    ),
  },
];

const MainMessagesContent = () => {
  const {
    loading,
    chats,
    paginationChats,
    // setInputSearchFieldByUserData,
    setStatusesOfChat,
    statusesOfChats,
    refreshUsersByStatus,
    setCurrentChat,
    currentChat,
  } = useContext(ChatsContext);
  const { setChats, localCountry } = useContext(AdminContext);
  const { joinChatAdmin, leaveChatAdmin, currentChatJoinedID } = useContext(SocketContext);
  const { openNotificationWithIcon } = useContext(NotificationContext);

  useEffect(() => {
    if (!chats?.chats?.length) return;
    // eslint-disable-next-line no-debugger
    chats.chats.forEach(({ id }) => {
      joinChatAdmin(id);
    });
  }, [chats?.chats]);

  useEffect(() => {
    return () => {
      leaveChatAdmin();
    };
  }, []);

  const firstLoadRef = useRef(true);
  const notLeaveFromIdChat = useRef<string>(null);
  useUpdateEffect(() => {
    if (firstLoadRef.current) {
      firstLoadRef.current = false;
      return;
    }
    leaveChatAdmin(null, notLeaveFromIdChat.current);
  }, [JSON.stringify(statusesOfChats)]);

  const changeStatus = useCallback(
    (status: STATUSES, notLeaveFromChatId?: string) => {
      divRef.current?.scrollTo({ left: 0, top: 0 });
      notLeaveFromIdChat.current = notLeaveFromChatId;
      setCurrentChat(null);
      paginationChats.current = { skip: 0, take: 10, total: 0 };
      setStatusesOfChat([status]);
    },
    [paginationChats, setStatusesOfChat],
  );

  const [searchParams, setSearchParams] = useSearchParams({ [PROP_NAME_MAIN_TAB]: "0" });
  const statusTabFilter = searchParams.get(PROP_NAME_MAIN_TAB);
  const choosenChatId = searchParams.get(PROP_NAME_CHOOSEN_CHAT_ID);
  // const choosenUserId = searchParams.get(PROP_NAME_CHOOSEN_USER_CHAT_ID);

  const { data: badges, runAsync } = useRequest(getSupportChatBadges, {
    refreshDeps: [localCountry?.iso],
    manual: true,
    onError: (err: any) => {
      openNotificationWithIcon("error", err.response?.data?.message || err.message);
    },
  });

  useEffect(() => {
    if (statusesOfChats) {
      runAsync(localCountry?.iso);
    }
  }, [statusesOfChats, localCountry?.iso]);

  const [items, setItems] = useState<any>([]);

  useEffect(() => {
    if (badges) setItems(getItems(badges[0]));
  }, [badges]);

  const switchChat = (activeKey: string, switchToChatId?: string) => {
    switch (activeKey) {
      case "0":
        changeStatus(STATUSES.WAIT, switchToChatId);
        break;
      case "1":
        changeStatus(STATUSES.IN_PROGRESS, switchToChatId);
        break;
      case "2":
        changeStatus(STATUSES.FINISHED, switchToChatId);
        break;
      default:
        changeStatus(STATUSES.WAIT, switchToChatId);
    }
  };

  // const firstLoadRef = useRef(true);
  useEffect(() => {
    if (chats?.chats && !loading && choosenChatId) {
      setTimeout(() => {
        // firstLoadRef.current = false;
        const choosenChat = chats.chats.find((chat) => chat.id === choosenChatId);
        localStorage.setItem("prevChatIdAdmin", choosenChat?.id || "0");

        setCurrentChat(choosenChat);
        currentChatJoinedID.current = choosenChatId;
      });
    }
  }, [chats, loading, choosenChatId]);

  const onChooseChat = (chat: ChatType) => {
    localStorage.setItem("prevChatIdAdmin", chat.id);
    searchParams.set(PROP_NAME_CHOOSEN_CHAT_ID, chat.id);
    searchParams.set(PROP_NAME_CHOOSEN_USER_CHAT_ID, chat.clientId);
    setChats(INIT_CHATS);
    setSearchParams(searchParams);
    currentChatJoinedID.current = chat.id;
  };

  useEffect(() => {
    if (statusTabFilter) {
      switchChat(statusTabFilter);
    }
  }, []);

  const dataLength = chats?.chats?.length || 0;
  const total = paginationChats.current?.total || 0;
  const hasMore = dataLength < total;

  const fetchMoreData = useCallback(() => {
    if (loading) return;
    const { skip = 0, take = 10, total = 0 } = paginationChats.current || {};
    if (skip + take >= total) return;
    paginationChats.current = { ...paginationChats.current, skip: skip + take };
    refreshUsersByStatus();
  }, [loading, paginationChats, refreshUsersByStatus]);
  const divRef = createRef<HTMLDivElement>();

  return (
    <div className={styles.container}>
      <TitleComponent text="Messages" />
      <div className={styles.buttonStatusWrapper}>
        <TabsComponent
          defaultActiveKey={searchParams.get(PROP_NAME_MAIN_TAB) || "0"}
          destroyInactiveTabPane
          activeKey={searchParams.get(PROP_NAME_MAIN_TAB) || "0"}
          onChange={(activeKey) => {
            searchParams.set(PROP_NAME_MAIN_TAB, items.find((v) => v.key === activeKey).key);
            searchParams.delete(PROP_NAME_CHOOSEN_CHAT_ID);
            searchParams.delete(PROP_NAME_CHOOSEN_USER_CHAT_ID);
            setSearchParams(searchParams, { replace: true });
            switchChat(items.find((v) => v.key === activeKey).key);
          }}
          items={items}
          className={styles.tabsComponent}
        />
      </div>
      {chats?.chats?.length === 0 && loading ? (
        <Spin style={{ position: "absolute", top: "50%", right: "50%", transform: "translate(50%, -50%)" }}></Spin>
      ) : (
        <div className={styles.messenger}>
          <div className={styles.connections} id="scrollableElem2" ref={divRef}>
            <InfiniteScroll
              dataLength={dataLength}
              hasMore={hasMore}
              className={styles.chat}
              loader={null}
              next={fetchMoreData}
              scrollableTarget="scrollableElem2"
            >
              {chats?.chats.map((chat) => (
                <Button
                  key={chat.id}
                  className={
                    currentChat?.id === chat.id ? `${styles.connectButton} ${styles.acrive}` : styles.connectButton
                  }
                  onClick={() => {
                    onChooseChat(chat);
                  }}
                >
                  {chat.client?.avatar ? (
                    <img className={styles.picture} src={getAvatarSrc(chat.client.avatar)} width={56} height={56} />
                  ) : (
                    <Avatar size={56} icon={<UserOutlined />} className={styles.avatar} />
                  )}
                  <div className={styles.textWrapper}>
                    <div className={styles.textTitle}>
                      <div>
                        {`${chat.client?.firstName} ${chat.client?.lastName}`}
                        {chat.partnerOnline ? <Badge status="success" className={styles.badgeSucess} /> : null}
                      </div>
                    </div>
                    {chat?.lastMessage?.[0].createdAt ? (
                      <div className={styles.supportText}>
                        {formatLastMessageDate(
                          chat?.lastMessage?.[0].createdAt.at(-1) === "Z"
                            ? chat?.lastMessage?.[0].createdAt
                            : chat?.lastMessage?.[0].createdAt + "Z",
                        )}
                      </div>
                    ) : null}
                  </div>
                  {chat.unreadMessages ? (
                    <div className={styles.pictureWrapper}>
                      <Badge status="warning" color="#FFCC4A" className={styles.badgeWarn} />
                    </div>
                  ) : null}
                </Button>
              ))}
            </InfiniteScroll>
            {hasMore && (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Spin />
              </div>
            )}
          </div>
          <Chat
            id={currentChat?.id}
            currentStatusChat={statusesOfChats[0]}
            closeChat={() => {
              setCurrentChat(null);
            }}
            userId={currentChat?.clientId}
            unreadMessages={currentChat?.unreadMessages}
            switchChat={(key: string) => {
              searchParams.set(PROP_NAME_MAIN_TAB, key);
              setSearchParams(searchParams);
              switchChat(key, currentChat?.id);
            }}
          />
        </div>
      )}
    </div>
  );
};

export default MainMessagesContent;
